import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {
  Avatar,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scrollbar } from 'react-scrollbars-custom';
import { routes } from 'utils/constants/routes';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import { getTableAvatarSx } from 'utils/avatarColors';

const getPastBookingDrawerWidth = (
  screenSizeDownSm: boolean,
  screenSizeDownMd: boolean,
  screenSizeUpLg: boolean,
) => {
  if (screenSizeDownSm) return '100%';
  if (screenSizeDownMd) return '92%';
  if (screenSizeUpLg) return '72%';
  return '82%';
};

export type PastBookingDetailRow = {
  lineItemId?: number | null;
  date?: number | null;
  customerName?: string | null;
  companyName?: string | null;
  venueName?: string | null;
  eventType?: string | null;
  eventSubtype?: string | null;
  revenue?: number | null;
  dues?: number | null;
};

interface PastBookingDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  selectedStatus: string;
  loading: boolean;
  rows: PastBookingDetailRow[];
}

const STATUS_DRAWER_THEMES: Record<string, { background: string; borderBottom: string; titleColor: string }> = {
  CONFIRMED: {
    background: 'linear-gradient(180deg, rgba(240, 253, 244, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
    borderBottom: '2px solid #86efac',
    titleColor: '#166534',
  },
  TENTATIVE: {
    background: 'linear-gradient(180deg, rgba(255, 251, 235, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
    borderBottom: '2px solid #fcd34d',
    titleColor: '#92400e',
  },
  WAITLISTED: {
    background: 'linear-gradient(180deg, rgba(239, 246, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
    borderBottom: '2px solid #93c5fd',
    titleColor: '#1e40af',
  },
  CANCELLED: {
    background: 'linear-gradient(180deg, rgba(254, 242, 242, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
    borderBottom: '2px solid #fca5a5',
    titleColor: '#991b1b',
  },
  PAYMENT_DUE: {
    background: 'linear-gradient(180deg, rgba(254, 252, 232, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
    borderBottom: '2px solid #fde047',
    titleColor: '#854d0e',
  },
  RFQ: {
    background: 'linear-gradient(180deg, rgba(255, 247, 237, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
    borderBottom: '2px solid #fdba74',
    titleColor: '#9a3412',
  },
};

const getInitials = (name?: string | null) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const formatEventDate = (value?: number | null) => {
  if (value == null) return '—';
  return moment(value).format('DD MMM YYYY');
};

const formatAmount = (value?: number | null) => {
  if (typeof value !== 'number') return '—';
  return formatCurrencyWithSymbol(value);
};

const getAmountColumnLabel = (status: string): string | null => {
  switch (status) {
    case 'CONFIRMED':
    case 'CANCELLED':
      return 'Revenue';
    case 'TENTATIVE':
    case 'WAITLISTED':
      return 'Potential';
    case 'PAYMENT_DUE':
      return 'Amount Due';
    case 'RFQ':
      return null;
    default:
      return 'Revenue';
  }
};

const getRowAmount = (status: string, row: PastBookingDetailRow) => {
  if (status === 'PAYMENT_DUE') return row.dues;
  return row.revenue;
};

const headerStyleForStatus = (status: string) =>
  STATUS_DRAWER_THEMES[status] ?? {
    background: '#FFFFFF',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    titleColor: 'inherit',
  };

const PastBookingDetailsDrawer: FC<PastBookingDetailsDrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  selectedStatus,
  loading,
  rows,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const drawerZIndex = theme.zIndex.modal;
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const drawerWidth = getPastBookingDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg);
  const headerStyle = headerStyleForStatus(selectedStatus);
  const isRfq = selectedStatus === 'RFQ';
  const amountColumnLabel = getAmountColumnLabel(selectedStatus);

  const tableColumns = useMemo(() => {
    const cols = ['Event Date', 'Customer', 'Venue', 'Event'];
    if (amountColumnLabel) cols.push(amountColumnLabel);
    return cols;
  }, [amountColumnLabel]);

  const handleRowClick = (row: PastBookingDetailRow) => {
    if (!row.lineItemId) return;
    if (isRfq) {
      navigate(`${routes.viewRfq}/${row.lineItemId}`);
      return;
    }
    navigate(`${routes.bookings}/${row.lineItemId}`);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: drawerZIndex }}
      transitionDuration={300}
      ModalProps={{
        keepMounted: false,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        sx: { zIndex: drawerZIndex },
      }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          maxWidth: { xs: '100vw', lg: 1100 },
          marginTop: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.25, flexShrink: 0, ...headerStyle }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2} sx={{ color: headerStyle.titleColor }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600} mt={0.25}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: 0.25 }}>
            <ClearOutlinedIcon color="primary" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Scrollbar style={{ width: '100%', height: '100%' }}>
          <Box px={2} py={1.25}>
            {loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" height={280} width="100%">
                <CircularProgress />
              </Box>
            ) : rows.length > 0 ? (
              <TableContainer
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflowX: 'auto',
                  backgroundColor: '#FFFFFF',
                }}>
                <Table size="small" stickyHeader sx={{ minWidth: amountColumnLabel ? 760 : 640 }}>
                  <TableHead>
                    <TableRow>
                      {tableColumns.map(col => (
                        <TableCell
                          key={col}
                          sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            backgroundColor: '#F9FAFB',
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                          }}>
                          {col}
                        </TableCell>
                      ))}
                      <TableCell sx={{ width: 28, backgroundColor: '#F9FAFB', p: 0.5 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => {
                      const clickable = Boolean(row.lineItemId);
                      const amount = getRowAmount(selectedStatus, row);
                      const eventSubtype = row.eventSubtype?.trim();

                      return (
                        <TableRow
                          key={row.lineItemId ?? idx}
                          hover={clickable}
                          onClick={() => clickable && handleRowClick(row)}
                          sx={{
                            cursor: clickable ? 'pointer' : 'default',
                            '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
                          }}>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: 13 }}>
                            {formatEventDate(row.date)}
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar sx={getTableAvatarSx(row.customerName, 36)}>{getInitials(row.customerName)}</Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }} noWrap>
                                  {row.customerName?.trim() || '—'}
                                </Typography>
                                {row.companyName?.trim() ? (
                                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.25 }} noWrap>
                                    {row.companyName.trim()}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 600, maxWidth: 160 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                              {row.venueName?.trim() || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }} noWrap>
                              {row.eventType?.trim() || '—'}
                            </Typography>
                            {eventSubtype ? (
                              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.25 }} noWrap>
                                {eventSubtype}
                              </Typography>
                            ) : null}
                          </TableCell>
                          {amountColumnLabel ? (
                            <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', fontSize: 13 }}>
                              {formatAmount(amount)}
                            </TableCell>
                          ) : null}
                          <TableCell sx={{ width: 28, p: 0.5, color: clickable ? 'primary.main' : 'text.disabled' }}>
                            {clickable ? <ChevronRightRoundedIcon sx={{ fontSize: 18 }} /> : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  No bookings found for this filter.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                  Try changing the date filter above to see results for a different time window.
                </Typography>
              </Box>
            )}
          </Box>
        </Scrollbar>
      </Box>
    </Drawer>
  );
};

export default PastBookingDetailsDrawer;
